import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato del Directorio.
 *
 * ## Los cinco endpoints NO cambian
 *
 * Los mismos que pedía `page-directory.js`, con los mismos parámetros.
 *
 * ## Lo que sí cambia: el `total` deja de tirarse
 *
 * `/users/directory` devuelve `{total, items}` (`users.py:209`) y el vanilla se
 * quedaba solo con `items`, pidiendo `limit: 100`. Con más de cien personas el
 * directorio enseñaba cien **sin decirlo**, y el CSV exportaba esas cien
 * anunciando «100 contactos exportados» — cierto y engañoso a la vez.
 *
 * Ahora el total viaja hasta la pantalla, que dice cuántas de cuántas se ven.
 *
 * ## `useAreas` vive aquí y en Celebraciones, a propósito
 *
 * Las dos usan la clave `['usuarios', 'areas']`, así que **comparten la caché**:
 * si una ya lo pidió, la otra no vuelve a preguntar. Lo compartido es la clave,
 * no el código — y eso es lo correcto, porque una feature no puede importar de
 * otra. El día que haga falta en una tercera, el hook sube a `components/`.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Lo que `_public_profile` (`users.py:342`) devuelve de cada persona.
 *
 *  Nota sobre `area_id`: es el id, no el nombre — el endpoint no manda el
 *  nombre. Para el CSV se resuelve en el cliente con `/users/areas`, que ya
 *  existe; ampliar `_public_profile` sería cambiar el contrato. */
export interface Persona {
  id: number;
  full_name: string;
  position?: string | null;
  area_id?: number | null;
  city?: string | null;
  avatar_url?: string | null;
  email?: string | null;
  phone?: string | null;
  extension?: string | null;
  skills?: string | null;
  bio?: string | null;
  birth_date_display?: string | null;
}

export interface PaginaDelDirectorio {
  total: number;
  items: Persona[];
}

export interface CursoTomado {
  course_id: number;
  course_title?: string | null;
  completed_at?: string | null;
  progress_pct?: number | null;
  has_certificate?: boolean;
}

export interface Area {
  id: number;
  name: string;
}

export interface ResultadoDeFotos {
  updated: number;
  not_found?: number;
  errors?: number;
  detail?: { not_found?: Array<{ file: string }> };
}

/** Cuántas personas se piden de una vez. Era 100 en el vanilla y se conserva:
 *  el endpoint acepta `skip`, así que subirlo no es la solución — lo que
 *  faltaba era **decir** que hay más, no traérselas todas. */
export const POR_PAGINA = 100;

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  directorio: (busqueda: string) => ['directorio', 'lista', busqueda] as const,
  perfil: (id: number) => ['directorio', 'perfil', id] as const,
  cursos: (id: number) => ['directorio', 'cursos', id] as const,
  // La misma clave que usa Celebraciones: comparten caché.
  areas: ['usuarios', 'areas'] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function useDirectorio(busqueda: string) {
  const limpia = busqueda.trim();
  return useQuery({
    queryKey: claves.directorio(limpia),
    queryFn: () =>
      api.get<PaginaDelDirectorio>('/users/directory', {
        limit: POR_PAGINA,
        ...(limpia ? { search: limpia } : {}),
      }),
    // La búsqueda anterior se queda en pantalla mientras llega la nueva, en vez
    // de vaciar la rejilla en cada tecla. El vanilla pintaba doce esqueletos
    // cada vez que alguien escribía una letra.
    placeholderData: (previa) => previa,
  });
}

/** Las áreas, y solo cuando hacen falta.
 *
 *  El directorio no las necesita para pintarse: el único que las usa es el CSV,
 *  para que su columna «Área» tenga nombres en vez de los `area_id` que devuelve
 *  `_public_profile`. Por eso no es un `useQuery` de la pantalla sino algo que se
 *  pide en el momento de exportar — así el Directorio sigue haciendo UNA
 *  petición al abrirse, las mismas que el vanilla.
 *
 *  Y si alguien ya pasó por Celebraciones, esto no pide nada: comparten la clave
 *  `['usuarios','areas']` y por tanto la caché. */
export function usePedirAreas() {
  const qc = useQueryClient();
  return () =>
    qc.fetchQuery({
      queryKey: claves.areas,
      queryFn: () => api.get<Area[]>('/users/areas'),
      staleTime: 10 * 60_000,
    });
}

/** La ficha de una persona: su perfil y sus cursos, en paralelo.
 *
 *  Son dos consultas y no una `Promise.all` como en el vanilla, y eso importa:
 *  si los cursos fallan, el perfil se ve igual. Antes un fallo en
 *  `/users/{id}/courses` tiraba el `Promise.all` entero y la ficha no se abría
 *  — se perdía el nombre, el correo y el teléfono por no poder listar cursos. */
export function usePerfil(id: number | null) {
  return useQuery({
    queryKey: claves.perfil(id ?? 0),
    queryFn: () => api.get<Persona & { area_name?: string | null }>(`/users/${id}/profile`),
    enabled: id != null,
  });
}

export function useCursosDe(id: number | null) {
  return useQuery({
    queryKey: claves.cursos(id ?? 0),
    queryFn: () => api.get<CursoTomado[]>(`/users/${id}/courses`),
    enabled: id != null,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

export function useSubirFoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, archivo }: { userId: number; archivo: File }) => {
      const datos = new FormData();
      datos.append('file', archivo);
      return api.upload<{ avatar_url: string }>(`/users/${userId}/avatar`, datos);
    },
    onSuccess: (_r, { userId }) => {
      qc.invalidateQueries({ queryKey: ['directorio', 'lista'] });
      qc.invalidateQueries({ queryKey: claves.perfil(userId) });
    },
  });
}

export function useSubirFotosMasivas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archivo: File) => {
      const datos = new FormData();
      datos.append('file', archivo);
      return api.upload<ResultadoDeFotos>('/users/avatars/bulk', datos);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['directorio', 'lista'] }),
  });
}
